import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { adminApi } from '../../api/client';
import RichTextEditor from '../../components/RichTextEditor';
import { SingleImageUpload, GalleryImageUpload } from '../../components/ImageUpload';

const inputClass = 'w-full px-3.5 py-2.5 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-shadow';

function Field({ label, error, children, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-1.5">
        {label}{hint && <span className="ml-1.5 text-xs text-muted-foreground font-normal">{hint}</span>}
      </label>
      {children}
      {error && <p className="text-xs text-destructive mt-1">Verplicht veld</p>}
    </div>
  );
}

const RICH_SECTIONS = [
  { key: 'usageHtml', label: 'Gebruiksaanwijzing' },
  { key: 'compositionHtml', label: 'Samenstelling' },
  { key: 'indicationsHtml', label: 'Indicaties' },
  { key: 'warningsHtml', label: 'Waarschuwingen' },
];
const LANGS = ['nl', 'fr', 'en'];

export default function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id && id !== 'new');
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [richTexts, setRichTexts] = useState({});
  const [images, setImages] = useState([]);
  const [imageUrl, setImageUrl] = useState('');
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [activeLang, setActiveLang] = useState('nl');

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: { vatRate: '0.06', currency: 'EUR', isActive: true, isFeatured: false, stock: 0 }
  });

  useEffect(() => {
    adminApi.listCategories().then(r => setCategories(r.data));
    if (isEdit) {
      adminApi.getProduct(id).then(r => {
        const p = r.data;
        // Separate rich text keys from RHF fields
        const richTextKeys = RICH_SECTIONS.flatMap(({ key }) => LANGS.map(lang => `${key}_${lang}`));
        const cleanData = {};
        for (const [k, v] of Object.entries(p)) {
          if (richTextKeys.includes(k) || k === 'images' || k === 'imageUrl' || k === 'heroImageUrl' || k === '_id' || k === 'created_at' || k === 'updated_at') continue;
          cleanData[k] = v;
        }
        // vatRate must be string for input
        cleanData.vatRate = String(p.vatRate ?? 0.06);
        // unitCostExVat: convert null to empty string for input
        if (cleanData.unitCostExVat === null || cleanData.unitCostExVat === undefined) {
          cleanData.unitCostExVat = '';
        }
        if (cleanData.compareAtPrice === null || cleanData.compareAtPrice === undefined) {
          cleanData.compareAtPrice = '';
        }
        reset(cleanData);
        setImages(p.images || []);
        setImageUrl(p.imageUrl || '');
        setHeroImageUrl(p.heroImageUrl || '');
        // Load rich texts
        const rt = {};
        for (const { key } of RICH_SECTIONS) {
          for (const lang of LANGS) {
            rt[`${key}_${lang}`] = p[`${key}_${lang}`] || '';
          }
        }
        setRichTexts(rt);
      });
    }
    // RICH_SECTIONS and LANGS are module-level constants, safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, reset]);

  const setRich = useCallback((field, val) => setRichTexts(prev => ({ ...prev, [field]: val })), []);

  const onSubmit = async (data) => {
    setSaving(true);
    setSaved(false);
    const payload = {
      ...data,
      ...richTexts,
      images,
      imageUrl,
      heroImageUrl,
      price: parseFloat(data.price) || 0,
      compareAtPrice: data.compareAtPrice ? parseFloat(data.compareAtPrice) : null,
      vatRate: parseFloat(data.vatRate) || 0.06,
      unitCostExVat: data.unitCostExVat ? parseFloat(data.unitCostExVat) : null,
      stock: parseInt(data.stock, 10) || 0,
    };
    // `stockFreeze` is operationally inert as of the 2026-04-26 stock
    // governance simplification: AA shippable stock is the sole source
    // of truth and the form must never send the field. Stripping any
    // stale value keeps the backend's audited freeze-write helper from
    // firing on a no-op.
    delete payload.stockFreeze;
    try {
      if (isEdit) {
        await adminApi.updateProduct(id, payload);
      } else {
        await adminApi.createProduct(payload);
      }
      setSaved(true);
      setTimeout(() => navigate('/admin/products'), 800);
    } catch (err) {
      alert(err.response?.data?.detail || 'Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="product-form-page" className="p-8 max-w-4xl">
      <Link to="/admin/products" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6 font-medium">
        <ArrowLeft className="w-4 h-4" /> Producten
      </Link>
      <h1 className="font-serif text-3xl font-light mb-8 text-foreground">{isEdit ? 'Product bewerken' : 'Nieuw product'}</h1>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

        {/* ── Names ── */}
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4 shadow-sm">
          <h2 className="font-serif text-base font-medium text-foreground">Productnaam</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Nederlands" error={errors.name_nl}><input data-testid="name-nl" {...register('name_nl', { required: true })} className={inputClass} /></Field>
            <Field label="Frans"><input data-testid="name-fr" {...register('name_fr')} className={inputClass} /></Field>
            <Field label="Engels"><input data-testid="name-en" {...register('name_en')} className={inputClass} /></Field>
          </div>
        </div>

        {/* ── Short descriptions ── */}
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4 shadow-sm">
          <h2 className="font-serif text-base font-medium text-foreground">Korte beschrijving</h2>
          <Field label="NL"><textarea {...register('description_nl')} rows={2} className={inputClass} /></Field>
          <Field label="FR"><textarea {...register('description_fr')} rows={2} className={inputClass} /></Field>
          <Field label="EN"><textarea {...register('description_en')} rows={2} className={inputClass} /></Field>
        </div>

        {/* ── Rich text — tabbed by language ── */}
        <div className="bg-card border border-border rounded-2xl p-6 space-y-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-base font-medium text-foreground">Productinhoud</h2>
            <div className="flex gap-1">
              {LANGS.map(l => (
                <button key={l} type="button" onClick={() => setActiveLang(l)} data-testid={`lang-tab-${l}`}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${activeLang === l ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted text-muted-foreground hover:bg-border'}`}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          {RICH_SECTIONS.map(({ key, label }) => (
            <RichTextEditor
              key={`${key}_${activeLang}`}
              label={label}
              value={richTexts[`${key}_${activeLang}`]}
              onChange={val => setRich(`${key}_${activeLang}`, val)}
            />
          ))}
        </div>

        {/* ── Pricing ── */}
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4 shadow-sm">
          <h2 className="font-serif text-base font-medium text-foreground">Prijs & BTW</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="Verkoopprijs (EUR incl. BTW)" error={errors.price}>
              <input data-testid="price" type="number" step="0.01" {...register('price', { required: true })} className={inputClass} />
            </Field>
            <Field label="Oude prijs" hint="voor doorhaling">
              <input data-testid="compare-price" type="number" step="0.01" {...register('compareAtPrice')} className={inputClass} />
            </Field>
            <Field label="BTW-tarief" hint="bv. 0.06 = 6%">
              <input data-testid="vat-rate" type="number" step="0.01" {...register('vatRate')} className={inputClass} />
            </Field>
            <Field label="Voorraad" hint="Commerce stock — auto-mirrored from Active Ants shippable stock by Ops > Active Ants. Edit only for emergencies; the next AA stock sync will overwrite manual edits.">
              <input data-testid="stock" type="number" {...register('stock')} className={inputClass} />
            </Field>
          </div>
        </div>

        {/* ── Classification & Meta ── */}
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4 shadow-sm">
          <h2 className="font-serif text-base font-medium text-foreground">Classificatie & meta</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Slug (URL)" error={errors.slug}>
              <input data-testid="slug" {...register('slug', { required: true })} className={inputClass} placeholder="acorus-complex-100ml" />
            </Field>
            <Field label="SKU / EAN">
              <input data-testid="sku" {...register('sku')} className={inputClass} />
            </Field>
            <Field label="Botanische naam">
              <input {...register('botanicalName')} className={inputClass} />
            </Field>
            <Field label="Herkomst">
              <input {...register('origin')} className={inputClass} />
            </Field>
            <Field label="Netto inhoud" hint="bv. 250ml, 120 caps">
              <input {...register('netContent')} className={inputClass} />
            </Field>
            <Field label="Productvorm" hint="bv. vloeistof, capsules">
              <input {...register('productForm')} className={inputClass} />
            </Field>
            <Field label="Categorie">
              <select data-testid="category-select" {...register('category')} className={inputClass}>
                <option value="">-- geen --</option>
                {categories.map(c => <option key={c._id} value={c._id}>{c.name_nl}</option>)}
              </select>
            </Field>
          </div>
          <div className="flex items-center gap-6 pt-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input data-testid="is-active" type="checkbox" {...register('isActive')} className="w-4 h-4 accent-primary rounded" /> Actief
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input data-testid="is-featured" type="checkbox" {...register('isFeatured')} className="w-4 h-4 accent-primary rounded" /> Uitgelicht
            </label>
          </div>
        </div>

        {/* ── B2B & Internal fields ── */}
        <div className="bg-card border border-border rounded-2xl p-6 space-y-4 shadow-sm">
          <h2 className="font-serif text-base font-medium text-foreground">B2B & intern</h2>
          <p className="text-xs text-muted-foreground -mt-2">Deze velden zijn alleen zichtbaar voor goedgekeurde B2B-gebruikers of intern voor admin.</p>
          <div className="grid grid-cols-2 gap-4">
            <Field label="NUT-nummer" hint="alleen zichtbaar voor B2B">
              <input data-testid="nut-number-input" {...register('nutNumber')} className={inputClass} placeholder="bv. NUT/PL/1234/2020" />
            </Field>
            <Field label="Kostprijs excl. BTW" hint="intern — niet op storefront">
              <input data-testid="unit-cost" type="number" step="0.01" {...register('unitCostExVat')} className={inputClass} placeholder="bv. 4.25" />
            </Field>
          </div>
          <Field label="Compendium URL" hint="PDF — alleen zichtbaar voor goedgekeurde B2B">
            <input data-testid="compendium-url" {...register('compendiumUrl')} className={inputClass} placeholder="https://..." />
          </Field>
        </div>

        {/* ── Images ── */}
        <div className="bg-card border border-border rounded-2xl p-6 space-y-5 shadow-sm">
          <h2 className="font-serif text-base font-medium text-foreground">Afbeeldingen</h2>
          <SingleImageUpload label="Hoofdafbeelding" value={imageUrl} onChange={setImageUrl} />
          <SingleImageUpload label="Hero afbeelding" hint="grote banner op productpagina" value={heroImageUrl} onChange={setHeroImageUrl} />
          <GalleryImageUpload images={images} onChange={setImages} />
        </div>

        {/* ── Save ── */}
        <div className="flex gap-3 pt-2">
          <button data-testid="save-product-btn" type="submit" disabled={saving}
            className={`inline-flex items-center gap-2 rounded-full px-8 py-3 text-sm font-semibold transition-all active:scale-95 shadow-sm disabled:opacity-50 ${saved ? 'bg-green-600 text-white' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}>
            {saved ? <><Check className="w-4 h-4" /> Opgeslagen</> : saving ? 'Opslaan...' : 'Opslaan'}
          </button>
          <Link to="/admin/products" className="px-8 py-3 border border-border rounded-full text-sm font-medium hover:bg-muted transition-colors">
            Annuleren
          </Link>
        </div>
      </form>
    </div>
  );
}
